import {
  mutationOptions,
  queryOptions,
  useMutation,
  useQuery,
  useQueryClient,
  type QueryClient,
} from '@tanstack/react-query'

import { getApi, unwrap } from '@/api/client'
import { qk } from '@/api/query-keys'
import type {
  Application,
  Template,
  TemplateInstallMode,
  TemplateList,
  TemplateUpdate,
} from '@/api/types'

export function templatesQuery(workspaceId: number) {
  return queryOptions({
    queryKey: qk.templates(workspaceId),
    queryFn: (): Promise<TemplateList> =>
      unwrap(
        getApi().GET('/api/workspaces/{workspace_id}/templates/', {
          params: {
            path: { workspace_id: workspaceId },
            query: { include_archived: true },
          },
        }),
      ),
  })
}

export function useTemplates(workspaceId: number) {
  return useQuery(templatesQuery(workspaceId))
}

export function templateQuery(workspaceId: number, templateId: string) {
  return queryOptions({
    queryKey: qk.template(workspaceId, templateId),
    // A missing template is the usual failure, and asking twice will not find
    // it. The page says so, so no toast either.
    retry: false,
    meta: { skipGlobalErrorHandler: true },
    queryFn: (): Promise<Template> =>
      unwrap(
        getApi().GET('/api/workspaces/{workspace_id}/templates/{template_id}/', {
          params: { path: { workspace_id: workspaceId, template_id: templateId } },
        }),
      ),
  })
}

export function useTemplate(workspaceId: number, templateId: string) {
  return useQuery(templateQuery(workspaceId, templateId))
}

export function updateTemplateMutation(queryClient: QueryClient) {
  return mutationOptions({
    mutationFn: ({
      workspaceId,
      templateId,
      body,
    }: {
      workspaceId: number
      templateId: string
      body: TemplateUpdate
    }): Promise<Template> =>
      unwrap(
        getApi().PATCH('/api/workspaces/{workspace_id}/templates/{template_id}/', {
          params: { path: { workspace_id: workspaceId, template_id: templateId } },
          body,
        }),
      ),
    onSuccess: (template, { workspaceId, templateId }) => {
      queryClient.setQueryData(qk.template(workspaceId, templateId), template)
      return queryClient.invalidateQueries({
        queryKey: qk.templates(workspaceId),
        exact: true,
      })
    },
  })
}

export function useUpdateTemplate() {
  return useMutation(updateTemplateMutation(useQueryClient()))
}

export function deleteTemplateMutation(queryClient: QueryClient) {
  return mutationOptions({
    mutationFn: ({
      workspaceId,
      templateId,
    }: {
      workspaceId: number
      templateId: string
    }) =>
      unwrap(
        getApi().DELETE('/api/workspaces/{workspace_id}/templates/{template_id}/', {
          params: { path: { workspace_id: workspaceId, template_id: templateId } },
        }),
      ),
    onSuccess: (_data, { workspaceId, templateId }) => {
      queryClient.removeQueries({ queryKey: qk.template(workspaceId, templateId) })
      return queryClient.invalidateQueries({
        queryKey: qk.templates(workspaceId),
        exact: true,
      })
    },
  })
}

export function useDeleteTemplate() {
  return useMutation(deleteTemplateMutation(useQueryClient()))
}

export function installTemplateMutation(queryClient: QueryClient) {
  return mutationOptions({
    mutationFn: ({
      workspaceId,
      templateId,
      mode,
    }: {
      workspaceId: number
      templateId: string
      mode: TemplateInstallMode
    }): Promise<Application[]> =>
      unwrap(
        getApi().POST('/api/workspaces/{workspace_id}/templates/{template_id}/install/', {
          params: { path: { workspace_id: workspaceId, template_id: templateId } },
          body: { mode },
        }),
      ),
    onSuccess: (_data, { workspaceId }) =>
      queryClient.invalidateQueries({ queryKey: qk.applications(workspaceId) }),
  })
}

export function useInstallTemplate() {
  return useMutation(installTemplateMutation(useQueryClient()))
}
